import {
  CoverageFileProcessingStatus,
  PeriodCoverageResponse,
  TrackCoverageResponse,
  YearCoverageBlockResponse,
} from '@interfaces/accounting-document-management/responses/document-coverage.response';

/**
 * Estado visual de uma célula (mês) da barra de cobertura de uma trilha. Deriva de
 * {@link PeriodCoverageResponse.status} e {@link PeriodCoverageResponse.isRequired} — nunca recalcula
 * regra de negócio, só traduz o que o backend já resolveu em algo que a UI sabe desenhar.
 *
 * `Approved` cai em `'sent'`: por decisão de produto não existe estado "Aprovado" na UI, ele renderiza
 * como "Enviado" (o mesmo visual de `Received`).
 */
export type CoverageCellState = 'pending' | 'sent' | 'analyzing' | 'rejected' | 'notRequired';

/**
 * Rótulo em português de cada {@link CoverageCellState}, conforme o protótipo (Claude Design,
 * "Component de envio de documentos", `Cobertura de documentos.dc.html`).
 */
export const CELL_STATE_LABEL: Record<CoverageCellState, string> = {
  pending: 'Pendente',
  sent: 'Enviado',
  analyzing: 'Em análise',
  rejected: 'Rejeitado',
  notRequired: 'Não exigido',
};

/**
 * Deriva o estado visual de uma célula a partir do status de conformidade resolvido pelo backend.
 *
 * Precedência: `Received`/`Approved` sempre viram `'sent'` e `Analyzing`/`Rejected` sempre mantêm sua
 * própria aparência, **independente** de {@link PeriodCoverageResponse.isRequired} — é assim que um
 * `BalanceteMensal` (nunca exigido) ainda consegue mostrar "Enviado" quando tem arquivo. Só quando o
 * status é literalmente `'Pending'` (nenhum registro de conformidade, ou registro nunca fechado)
 * `isRequired` decide entre `'pending'` (exigido, faltando) e `'notRequired'` (mês futuro, ou tipo
 * complementar como `BalanceteMensal`).
 */
export function resolveCellState(period: PeriodCoverageResponse): CoverageCellState {
  switch (period.status) {
    case 'Received':
    case 'Approved':
      return 'sent';
    case 'Analyzing':
      return 'analyzing';
    case 'Rejected':
      return 'rejected';
    default:
      return period.isRequired ? 'pending' : 'notRequired';
  }
}

const MONTH_SHORT_LABELS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

const MONTH_LONG_LABELS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

/** Abreviação em português de um mês (1–12), ex.: `monthShortLabel(3) === 'mar'`. */
export function monthShortLabel(month: number): string {
  return MONTH_SHORT_LABELS[month - 1] ?? String(month);
}

/** Nome por extenso em português de um mês (1–12), ex.: `monthLongLabel(3) === 'Março'`. */
export function monthLongLabel(month: number): string {
  return MONTH_LONG_LABELS[month - 1] ?? String(month);
}

/**
 * Tipo de {@link PendencyChip}: `'pending-annual'` é o anual em falta de uma trilha do regime
 * `"Anual"` (DRE/Balanço); `'pending-month'`/`'pending-year'` são, respectivamente, um mês isolado ou
 * o ano inteiro (colapsado) pendente na trilha `"Mensal"` (Faturamento); `'rejected'`/`'analyzing'`
 * são alertas, em qualquer regime.
 */
export type PendencyChipKind =
  | 'pending-annual'
  | 'pending-month'
  | 'pending-year'
  | 'rejected'
  | 'analyzing';

/**
 * Um chip clicável da faixa de pendências do resumo de cobertura. Já carrega o período completo
 * (`documentType`/`startMonth`/`endMonth`) a selecionar no clique — quem constrói o chip é quem sabe
 * de qual trilha e período ele veio, então a página só repassa esses campos para `selectPeriod`, sem
 * precisar resolvê-los de novo.
 */
export interface PendencyChip {
  kind: PendencyChipKind;
  year: number;
  documentType: 'Dre' | 'Balanco' | 'Faturamento';
  startMonth: number;
  endMonth: number;
  label: string;
}

/**
 * Constrói os chips de pendência varrendo as três trilhas de cada exercício (`YearCoverageBlockResponse`).
 *
 * As duas trilhas do regime `"Anual"` (DRE, Balanço) só têm uma exigência — o próprio anual — então
 * cada uma gera no máximo **um** chip, nomeando a trilha (`"Falta o anual do DRE 2024"`). A trilha do
 * regime `"Mensal"` (Faturamento) segue a regra anterior por mês: se **todos** os meses exigidos do
 * ano estão pendentes, colapsa num único chip `'pending-year'`; caso contrário, um `'pending-month'`
 * por mês pendente (`"Faturamento out/24"`). Em qualquer regime, todo período com status
 * `Rejected`/`Analyzing` gera seu próprio chip de alerta.
 *
 * É a correção do achado da revisão da Task 4: antes desta função saber de trilhas, um exercício com
 * DRE/Balanço já cobertos e só o Faturamento pendente não gerava chip nenhum (o código só olhava para
 * `annual`/`months` no nível do ano, e via o slot anual presente e coberto) — o card de resumo lia
 * como "sem pendência" uma empresa que, na verdade, devia meses de faturamento.
 */
export function buildPendencyChips(years: YearCoverageBlockResponse[]): PendencyChip[] {
  const chips: PendencyChip[] = [];

  for (const year of years) {
    for (const track of year.tracks) {
      if (track.requirementMode === 'Anual') {
        chips.push(...buildAnnualTrackChips(year.year, track));
      } else {
        chips.push(...buildMonthlyTrackChips(year.year, track));
      }
    }
  }

  return chips;
}

/** Chips da trilha do regime `"Anual"` (DRE/Balanço) — no máximo um, sobre o próprio slot anual. */
function buildAnnualTrackChips(year: number, track: TrackCoverageResponse): PendencyChip[] {
  const annual = track.annual;
  if (!annual) return [];

  const state = resolveCellState(annual);

  // Rejeitado/em análise gera alerta INDEPENDENTE de o anual já ser exigido ou não — mesmo invariante
  // de buildMonthlyTrackChips (e do código anterior a esta task, que iterava sobre todos os períodos,
  // exigidos ou não). Um anual enviado adiantado (ano corrente, ainda não fechado, isRequired: false)
  // que caia em Rejected/Analyzing não pode ficar sem chip só por causa disso — achado da rodada 1 de
  // correção: com '!annual.isRequired' como guarda de entrada, essa pendência sumia do card, e se fosse
  // a única pendência real da empresa, 'hasPendency()' virava false e 'Nada pendente — cobertura
  // completa.' reaparecia (a mesma classe de defeito que a correção obrigatória #2 elimina).
  if (state === 'rejected' || state === 'analyzing') {
    return [
      {
        kind: state,
        year,
        documentType: track.documentType,
        startMonth: 1,
        endMonth: 12,
        label: `${track.label} ${year} · anual ${CELL_STATE_LABEL[state].toLowerCase()}`,
      },
    ];
  }

  if (!annual.isRequired || state !== 'pending') return [];

  return [
    {
      kind: 'pending-annual',
      year,
      documentType: track.documentType,
      startMonth: 1,
      endMonth: 12,
      label: `Falta o anual do ${track.label} ${year}`,
    },
  ];
}

/** Chips da trilha do regime `"Mensal"` (Faturamento) — um por mês pendente/rejeitado/em análise. */
function buildMonthlyTrackChips(year: number, track: TrackCoverageResponse): PendencyChip[] {
  const chips: PendencyChip[] = [];
  const periods = track.months;
  const requiredPeriods = periods.filter(p => p.isRequired);
  const pendingPeriods = requiredPeriods.filter(p => resolveCellState(p) === 'pending');

  if (pendingPeriods.length > 0 && pendingPeriods.length === requiredPeriods.length) {
    const anchor = pendingPeriods[0].month;
    chips.push({
      kind: 'pending-year',
      year,
      documentType: track.documentType,
      startMonth: anchor,
      endMonth: anchor,
      label: `${track.label} ${year} · ano todo (${pendingPeriods.length} ${pendingPeriods.length === 1 ? 'mês' : 'meses'})`,
    });
  } else {
    for (const period of pendingPeriods) {
      chips.push({
        kind: 'pending-month',
        year,
        documentType: track.documentType,
        startMonth: period.month,
        endMonth: period.month,
        label: `${track.label} ${monthShortLabel(period.month)}/${String(year).slice(-2)}`,
      });
    }
  }

  for (const period of periods) {
    const state = resolveCellState(period);
    if (state !== 'rejected' && state !== 'analyzing') continue;

    chips.push({
      kind: state,
      year,
      documentType: track.documentType,
      startMonth: period.month,
      endMonth: period.month,
      label: `${track.label} ${monthShortLabel(period.month)}/${String(year).slice(-2)} · ${CELL_STATE_LABEL[state].toLowerCase()}`,
    });
  }

  return chips;
}

/**
 * Classe CSS (sufixo BEM) de cada {@link CoverageCellState}, para o swatch/célula visual da barra de
 * mês de `year-coverage`.
 */
export const CELL_STATE_CLASS: Record<CoverageCellState, string> = {
  pending: 'pending',
  sent: 'sent',
  analyzing: 'analyzing',
  rejected: 'rejected',
  notRequired: 'not-required',
};

/**
 * Rótulo em português do status de **processamento** de um arquivo (`CoverageFileResponse.status`).
 * **Não** é o status de conformidade do período (esse é {@link CELL_STATE_LABEL}) — ver a nota de
 * divergência consciente no README da feature: por arquivo só existe o estado da extração
 * (fila/processando/processado/erro), nunca "rejeitado"/"em análise" (que são do período).
 */
export const FILE_PROCESSING_STATUS_LABEL: Record<CoverageFileProcessingStatus, string> = {
  InQueue: 'Na fila',
  Processing: 'Processando',
  Processed: 'Processado',
  Error: 'Erro',
};
